// Projects, modelled exactly like the dashboard's DashboardBridge: a Keycloak
// realm paired with a pyst API path. Kept in localStorage, same as over there.
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Bridge {
    id: string;
    name: string;
    keycloakRealm: string;
    apiPath: string;
}

const BRIDGES_KEY = 'plots-bridges';
const ACTIVE_BRIDGE_KEY = 'plots-active-bridge-id';

/**
 * Earlier builds seeded a project from the build environment under this id and
 * persisted it with the others. It was never something the user added, so it is
 * dropped on read.
 */
const LEGACY_SEEDED_ID = 'default';

function canUseStorage(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    try {
        return !!window.localStorage;
    } catch {
        return false;
    }
}

function readStorageItem(key: string): string | null {
    if (!canUseStorage()) {
        return null;
    }
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorageItem(key: string, value: string): void {
    if (!canUseStorage()) {
        return;
    }
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Keep in-memory state usable even when storage is unavailable.
    }
}

/**
 * Dashboard origin for a project, derived from its API path.
 *
 * The two are the same deployment with different hostnames — `api.<name>.iotbridge.se`
 * serves pyst, `<name>.iotbridge.se` serves the dashboard — and `<name>` differs per
 * project, so it has to be derived rather than configured. A local pyst is the
 * exception: there is no `api.` to strip, and the dashboard dev server sits on 4200.
 */
export function dashboardOriginFromApiPath(apiPath: string): string {
    const trimmed = (apiPath || '').trim();
    if (!trimmed) {
        return '';
    }
    let url: URL;
    try {
        url = new URL(trimmed);
    } catch {
        return '';
    }

    const host = url.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
        return `${url.protocol}//${host}:4200`;
    }
    const withoutApi = host.replace(/^api\./i, '');
    const port = url.port ? `:${url.port}` : '';
    return `${url.protocol}//${withoutApi}${port}`;
}

function randomId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `bridge-${Date.now()}-${Math.round(Math.random() * 100000)}`;
}

function normalizeBridge(bridge: Partial<Bridge>): Bridge {
    const keycloakRealm = (bridge.keycloakRealm || '').trim();
    const apiPath = (bridge.apiPath || '').trim();
    return {
        id: (bridge.id || randomId()).trim(),
        name: (bridge.name || keycloakRealm || 'Project').trim(),
        keycloakRealm,
        apiPath,
    };
}

/** The projects the user has added — nothing else. Empty until they add one. */
export function readBridges(): Bridge[] {
    if (!canUseStorage()) {
        return [];
    }
    try {
        const parsed = JSON.parse(readStorageItem(BRIDGES_KEY) || '[]');
        const stored = Array.isArray(parsed) ? parsed : [];
        return stored
            .filter((bridge: Partial<Bridge>) => bridge?.id !== LEGACY_SEEDED_ID)
            .map((bridge: Partial<Bridge>) => normalizeBridge(bridge))
            .filter((bridge: Bridge) => bridge.keycloakRealm && bridge.apiPath);
    } catch {
        return [];
    }
}

// The last active project, if it still exists. Used to preselect the picker, and —
// once the user has confirmed a project for the session — to build the Keycloak
// client for its realm.
export function readActiveBridge(): Bridge | null {
    const bridges = readBridges();
    const activeId = readStorageItem(ACTIVE_BRIDGE_KEY);
    return bridges.find((bridge) => bridge.id === activeId) ?? null;
}

@Injectable({ providedIn: 'root' })
export class BridgeService {
    private bridgesSubject = new BehaviorSubject<Bridge[]>(readBridges());
    private activeBridgeSubject = new BehaviorSubject<Bridge | null>(readActiveBridge());

    public bridges$ = this.bridgesSubject.asObservable();
    public activeBridge$ = this.activeBridgeSubject.asObservable();

    get bridges(): Bridge[] {
        return this.bridgesSubject.value;
    }

    get activeBridge(): Bridge | null {
        return this.activeBridgeSubject.value;
    }

    /** Dashboard origin for the active project, derived from its API path. */
    get dashboardOrigin(): string {
        return dashboardOriginFromApiPath(this.activeBridge?.apiPath ?? '');
    }

    saveBridge(bridge: Partial<Bridge>): Bridge {
        const normalized = normalizeBridge(bridge);
        const bridges = this.bridges.filter((item) => item.id !== normalized.id);
        bridges.push(normalized);
        this.writeBridges(bridges);
        return normalized;
    }

    removeBridge(id: string): void {
        const wasActive = this.activeBridge?.id === id;
        const remaining = this.bridges.filter((bridge) => bridge.id !== id);
        this.writeBridges(remaining);
        if (wasActive) {
            // The removed project was the signed-in one: back to the picker.
            writeStorageItem(ACTIVE_BRIDGE_KEY, '');
            this.activeBridgeSubject.next(null);
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        }
    }

    /** Make a project active without reloading; the picker uses this before login. */
    activate(id: string): Bridge | null {
        const bridge = this.bridges.find((item) => item.id === id) ?? null;
        writeStorageItem(ACTIVE_BRIDGE_KEY, bridge?.id ?? '');
        this.activeBridgeSubject.next(bridge);
        return bridge;
    }

    // Switching realm means a new Keycloak instance, so reload like the dashboard does.
    switchAndReload(id: string): void {
        this.activate(id);
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    }

    private writeBridges(bridges: Bridge[]): void {
        const next = bridges
            .map((bridge) => normalizeBridge(bridge))
            .filter((bridge) => bridge.keycloakRealm && bridge.apiPath);
        writeStorageItem(BRIDGES_KEY, JSON.stringify(next));
        this.bridgesSubject.next(next);
        const activeId = this.activeBridge?.id;
        this.activeBridgeSubject.next(next.find((bridge) => bridge.id === activeId) ?? null);
    }
}
